import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Newspaper, Download, FileText, Search, Loader2, CheckCircle, XCircle, RefreshCw, Calendar, AlertCircle, ExternalLink } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

function AdminBoeScans() {
  const [scans, setScans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [escaneando, setEscaneando] = useState(false);
  const [scanDetalle, setScanDetalle] = useState(null);
  const [items, setItems] = useState([]);
  const [cargandoDetalle, setCargandoDetalle] = useState(false);
  const [descargandoPdf, setDescargandoPdf] = useState(null);
  const navigate = useNavigate();
  const { usuario, loading: authLoading } = useAuth();

  useEffect(() => {
    if (authLoading) return;
    if (usuario?.rol !== 'admin') {
      navigate('/');
      return;
    }
    cargarScans();
  }, [authLoading, usuario]);

  const cargarScans = async () => {
    setLoading(true);
    try {
      const res = await api.get('/dgt/boe/scans?limit=50');
      setScans(res.data.scans || []);
    } catch (error) {
      console.error('Error cargando scans BOE:', error);
    } finally {
      setLoading(false);
    }
  };

  const escanearAhora = async () => {
    if (escaneando) return;
    setEscaneando(true);
    try {
      await api.post('/dgt/boe/escanear');
      await cargarScans();
    } catch (error) {
      console.error('Error ejecutando scan:', error);
      alert('Error al ejecutar scan: ' + (error.response?.data?.error || error.message));
    } finally {
      setEscaneando(false);
    }
  };

  const verDetalle = async (scan) => {
    setCargandoDetalle(true);
    setScanDetalle(scan);
    try {
      const res = await api.get(`/dgt/boe/scans/${scan.id}`);
      setItems(res.data.items || []);
    } catch (error) {
      console.error('Error cargando detalle:', error);
      setItems([]);
    } finally {
      setCargandoDetalle(false);
    }
  };

  const cerrarDetalle = () => {
    setScanDetalle(null);
    setItems([]);
  };

  const descargarPdf = async (itemId) => {
    setDescargandoPdf(itemId);
    try {
      const res = await api.get(`/dgt/boe/pdf/${itemId}`);
      if (res.data.success && res.data.download_url) {
        window.open(res.data.download_url, '_blank');
      }
    } catch (error) {
      const msg = error.response?.data?.error || error.message;
      alert('Error descargando PDF: ' + msg);
    } finally {
      setDescargandoPdf(null);
    }
  };

  const formatFecha = (fecha) => {
    if (!fecha) return '-';
    return new Date(fecha).toLocaleDateString('es-ES', {
      day: '2-digit', month: '2-digit', year: 'numeric'
    });
  };

  const formatFechaHora = (fecha) => {
    if (!fecha) return '-';
    return new Date(fecha).toLocaleString('es-ES', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  const estadoBadge = (estado) => {
    const estilos = {
      completado: 'bg-emerald-100 text-emerald-700',
      error: 'bg-red-100 text-red-700',
      en_proceso: 'bg-amber-100 text-amber-700',
      sin_resultados: 'bg-slate-100 text-slate-600'
    };
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${estilos[estado] || 'bg-slate-100 text-slate-600'}`}>
        {estado || 'desconocido'}
      </span>
    );
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/admin/dashboard')} className="p-2 hover:bg-slate-200 rounded-lg transition-colors">
              <ArrowLeft className="w-5 h-5 text-slate-600" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-slate-900 font-display flex items-center gap-2">
                <Newspaper className="w-7 h-7 text-primary-600" />
                BOE Scanner
              </h1>
              <p className="text-slate-600 text-sm">Tablón Edictal Único — Notificaciones de tráfico</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={cargarScans}
              className="px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-100 transition-colors flex items-center gap-2 text-sm"
            >
              <RefreshCw className="w-4 h-4" />
              Actualizar
            </button>
            <button
              onClick={escanearAhora}
              disabled={escaneando}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center gap-2 text-sm disabled:opacity-50"
            >
              {escaneando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              {escaneando ? 'Escaneando...' : 'Escanear ahora'}
            </button>
          </div>
        </div>

        {/* Stats resumen */}
        {scans.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-xl p-4 border border-slate-200">
              <p className="text-sm text-slate-600">Total Scans</p>
              <p className="text-2xl font-bold text-slate-900">{scans.length}</p>
            </div>
            <div className="bg-white rounded-xl p-4 border border-slate-200">
              <p className="text-sm text-slate-600">Notificaciones</p>
              <p className="text-2xl font-bold text-slate-900">{scans.reduce((s, sc) => s + (sc.total_notificaciones || 0), 0)}</p>
            </div>
            <div className="bg-white rounded-xl p-4 border border-slate-200">
              <p className="text-sm text-emerald-600">Matches</p>
              <p className="text-2xl font-bold text-emerald-700">{scans.reduce((s, sc) => s + (sc.total_matches || 0), 0)}</p>
            </div>
            <div className="bg-white rounded-xl p-4 border border-slate-200">
              <p className="text-sm text-primary-600">Multas Creadas</p>
              <p className="text-2xl font-bold text-primary-700">{scans.reduce((s, sc) => s + (sc.total_multas_creadas || 0), 0)}</p>
            </div>
          </div>
        )}

        {/* Tabla de Scans */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50">
                  <th className="text-left py-3 px-4 font-medium text-slate-700 text-sm">Fecha BOE</th>
                  <th className="text-left py-3 px-4 font-medium text-slate-700 text-sm">Fecha Scan</th>
                  <th className="text-center py-3 px-4 font-medium text-slate-700 text-sm">Notif.</th>
                  <th className="text-center py-3 px-4 font-medium text-slate-700 text-sm">PDFs</th>
                  <th className="text-center py-3 px-4 font-medium text-slate-700 text-sm">Filas</th>
                  <th className="text-center py-3 px-4 font-medium text-slate-700 text-sm">Matches</th>
                  <th className="text-center py-3 px-4 font-medium text-slate-700 text-sm">Creadas</th>
                  <th className="text-center py-3 px-4 font-medium text-slate-700 text-sm">Dupl.</th>
                  <th className="text-center py-3 px-4 font-medium text-slate-700 text-sm">Errores</th>
                  <th className="text-center py-3 px-4 font-medium text-slate-700 text-sm">Estado</th>
                </tr>
              </thead>
              <tbody>
                {scans.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="text-center py-12 text-slate-500">
                      <Newspaper className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                      <p>No hay scans registrados</p>
                      <p className="text-sm mt-1">Ejecuta un scan manual para empezar</p>
                    </td>
                  </tr>
                ) : (
                  scans.map(scan => (
                    <tr
                      key={scan.id}
                      onClick={() => verDetalle(scan)}
                      className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors"
                    >
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-slate-400" />
                          <span className="text-sm font-medium text-slate-900">{formatFecha(scan.fecha_boe)}</span>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-sm text-slate-600">{formatFechaHora(scan.fecha_scan)}</td>
                      <td className="py-3 px-4 text-center text-sm">{scan.total_notificaciones || 0}</td>
                      <td className="py-3 px-4 text-center text-sm">{scan.total_pdfs_procesados || 0}</td>
                      <td className="py-3 px-4 text-center text-sm">{scan.total_filas_extraidas || 0}</td>
                      <td className="py-3 px-4 text-center">
                        {(scan.total_matches || 0) > 0 ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full text-xs font-medium">
                            <CheckCircle className="w-3 h-3" />
                            {scan.total_matches}
                          </span>
                        ) : (
                          <span className="text-sm text-slate-400">0</span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-center">
                        {(scan.total_multas_creadas || 0) > 0 ? (
                          <span className="font-medium text-primary-700 text-sm">{scan.total_multas_creadas}</span>
                        ) : (
                          <span className="text-sm text-slate-400">0</span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-center text-sm text-slate-500">{scan.total_duplicadas || 0}</td>
                      <td className="py-3 px-4 text-center">
                        {(scan.total_errores || 0) > 0 ? (
                          <span className="text-red-600 text-sm font-medium">{scan.total_errores}</span>
                        ) : (
                          <span className="text-sm text-slate-400">0</span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-center">{estadoBadge(scan.estado)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Modal Detalle */}
        {scanDetalle && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={cerrarDetalle}>
            <div className="bg-white rounded-2xl shadow-xl max-w-5xl w-full max-h-[85vh] overflow-hidden" onClick={e => e.stopPropagation()}>
              {/* Header */}
              <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-bold text-slate-900">
                    Scan BOE — {formatFecha(scanDetalle.fecha_boe)}
                  </h2>
                  <p className="text-sm text-slate-500">
                    {scanDetalle.total_notificaciones || 0} notificaciones, {scanDetalle.total_matches || 0} matches, {scanDetalle.total_multas_creadas || 0} multas creadas
                  </p>
                </div>
                <button onClick={cerrarDetalle} className="p-2 hover:bg-slate-100 rounded-lg transition-colors">
                  <XCircle className="w-5 h-5 text-slate-400" />
                </button>
              </div>

              {/* Contenido */}
              <div className="overflow-auto max-h-[calc(85vh-80px)] p-6">
                {cargandoDetalle ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
                  </div>
                ) : items.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <FileText className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                    <p>No hay items para este scan</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-slate-200 bg-slate-50">
                          <th className="text-left py-2 px-3 font-medium text-slate-700 text-xs">BOE ID</th>
                          <th className="text-left py-2 px-3 font-medium text-slate-700 text-xs">Organismo</th>
                          <th className="text-left py-2 px-3 font-medium text-slate-700 text-xs">Matricula</th>
                          <th className="text-left py-2 px-3 font-medium text-slate-700 text-xs">Expediente</th>
                          <th className="text-left py-2 px-3 font-medium text-slate-700 text-xs">CIF</th>
                          <th className="text-center py-2 px-3 font-medium text-slate-700 text-xs">Match</th>
                          <th className="text-center py-2 px-3 font-medium text-slate-700 text-xs">Multa</th>
                          <th className="text-center py-2 px-3 font-medium text-slate-700 text-xs">PDF</th>
                          <th className="text-left py-2 px-3 font-medium text-slate-700 text-xs">Error</th>
                        </tr>
                      </thead>
                      <tbody>
                        {items.map(item => (
                          <tr key={item.id} className="border-b border-slate-100 hover:bg-slate-50">
                            <td className="py-2 px-3 text-xs font-mono text-slate-700">{item.boe_id || '-'}</td>
                            <td className="py-2 px-3 text-xs text-slate-600 max-w-[200px] truncate" title={item.organismo}>
                              {item.organismo || '-'}
                            </td>
                            <td className="py-2 px-3">
                              {item.matricula ? (
                                <span className="px-2 py-0.5 bg-slate-100 rounded text-xs font-mono font-medium">{item.matricula}</span>
                              ) : (
                                <span className="text-xs text-slate-400">-</span>
                              )}
                            </td>
                            <td className="py-2 px-3 text-xs font-mono text-slate-600">{item.expediente || '-'}</td>
                            <td className="py-2 px-3 text-xs font-mono text-slate-600">{item.cif || '-'}</td>
                            <td className="py-2 px-3 text-center">
                              {item.matched ? (
                                <CheckCircle className="w-4 h-4 text-emerald-500 mx-auto" />
                              ) : (
                                <XCircle className="w-4 h-4 text-slate-300 mx-auto" />
                              )}
                            </td>
                            <td className="py-2 px-3 text-center">
                              {item.multa_id ? (
                                <button
                                  onClick={() => navigate(`/admin/multas/${item.multa_id}/editar`)}
                                  className="text-xs text-primary-600 hover:underline font-medium"
                                >
                                  #{item.multa_id}
                                </button>
                              ) : (
                                <span className="text-xs text-slate-400">-</span>
                              )}
                            </td>
                            <td className="py-2 px-3 text-center">
                              {item.pdf_s3_url ? (
                                <button
                                  onClick={() => descargarPdf(item.id)}
                                  disabled={descargandoPdf === item.id}
                                  className="p-1 hover:bg-primary-50 rounded transition-colors text-primary-600 disabled:opacity-50"
                                  title="Descargar PDF"
                                >
                                  {descargandoPdf === item.id ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <Download className="w-4 h-4" />
                                  )}
                                </button>
                              ) : (
                                <span className="text-xs text-slate-300" title="PDF no almacenado en S3">-</span>
                              )}
                            </td>
                            <td className="py-2 px-3">
                              {item.error ? (
                                <span className="text-xs text-red-600 flex items-center gap-1" title={item.error}>
                                  <AlertCircle className="w-3 h-3 flex-shrink-0" />
                                  <span className="truncate max-w-[150px]">{item.error}</span>
                                </span>
                              ) : (
                                <span className="text-xs text-slate-400">-</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Info del scan */}
                {scanDetalle.error && (
                  <div className="mt-4 p-3 bg-red-50 rounded-lg border border-red-200">
                    <p className="text-sm text-red-700 flex items-center gap-2">
                      <AlertCircle className="w-4 h-4" />
                      {scanDetalle.error}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminBoeScans;
